import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, Button, CircularProgress, IconButton, Stack, TextField } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { PRESET_PROMPTS } from './constants';

export interface FundOpportunityChatInputProps {
  variant: 'initial' | 'chat';
  inputValue: string;
  isProcessing: boolean;
  onInputChange: (value: string) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onPresetClick: (prompt: string) => void;
}

const FundOpportunityChatInput: React.FC<FundOpportunityChatInputProps> = ({
  variant,
  inputValue,
  isProcessing,
  onInputChange,
  onSubmit,
  onPresetClick,
}) => {
  const isInitial = variant === 'initial';

  return (
    <Stack
      spacing={isInitial ? 3 : 2}
      alignItems={isInitial ? 'center' : 'stretch'}
      sx={{
        width: '100%',
        maxWidth: '720px',
        mx: 'auto',
        ...(isInitial
          ? {}
          : {
              mt: { xs: 2, md: 3 },
            }),
      }}
    >
      <Box component="form" onSubmit={onSubmit} sx={{ width: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderRadius: '32px',
            border: '1px solid #123833',
            backgroundColor: '#ffffff',
            px: { xs: 2, md: 3 },
            py: 1.5,
          }}
        >
          <TextField
            value={inputValue}
            onChange={(event) => onInputChange(event.target.value)}
            placeholder="Ask me anything about this opportunity"
            variant="standard"
            fullWidth
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '1rem',
                color: Palette.tone1.text,
              },
            }}
            multiline
            minRows={1}
            maxRows={4}
          />

          <IconButton
            type="submit"
            disabled={!inputValue.trim() || isProcessing}
            sx={{
              backgroundColor: Palette.default.buttonBg,
              color: Palette.default.buttonText,
              width: 48,
              height: 48,
              borderRadius: '50%',
              '&:hover': {
                backgroundColor: Palette.tone4.activeBg,
              },
            }}
          >
            {isProcessing ? (
              <CircularProgress size={22} sx={{ color: 'white' }} />
            ) : (
              <ArrowUpwardIcon />
            )}
          </IconButton>
        </Box>
      </Box>

      {isInitial && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          alignItems="stretch"
          sx={{ width: '100%' }}
        >
          {PRESET_PROMPTS.map((prompt) => (
            <Button
              key={prompt}
              type="button"
              variant="outlined"
              onClick={() => onPresetClick(prompt)}
              disabled={isProcessing}
              sx={{
                borderRadius: '20px',
                px: 3,
                py: 1.75,
                borderColor: '#274B42',
                color: '#123833',
                textTransform: 'none',
                backgroundColor: Palette.tone2.background,
                '&:hover': {
                  backgroundColor: '#e1e8e3',
                  borderColor: '#1c3b34',
                },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: '240px' },
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {prompt}
            </Button>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default FundOpportunityChatInput;
